import SearchBar from "@via/components/search-bar.tsx";
import { classNames } from "@via/utils/helpers";
import { PipelineViewType } from "common";
import { useDebounceCallback } from "usehooks-ts";

interface PipelineFilterProps {
  setSearchText?: (value: string) => void;
  selectedPipelineType: PipelineViewType[];
  setSelectedPipelineType: (types: PipelineViewType[]) => void;
  setUserNameText?: (value: string) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  userNameText?: string;
  searchText?: string;
}

export default function PipelineFilter({
  setSearchText,
  selectedPipelineType,
  setSelectedPipelineType,
  setUserNameText,
  setPage,
  setPageSize,
  userNameText = "",
  searchText = "",
}: PipelineFilterProps) {
  const debouncedSearch = useDebounceCallback(value => {
    setSearchText?.(value);
    setPage(0);
    setPageSize(100);
  }, 300);

  const debouncedUserName = useDebounceCallback(value => {
    setUserNameText?.(value);
    setPage(0);
    setPageSize(100);
  }, 300);

  const handleSelectPipelineType = (type: PipelineViewType) => {
    setSelectedPipelineType([type]);
  };

  return (
    <div className="flex flex-col items-baseline justify-between gap-2 md:flex-row">
      {setSearchText && (
        <div className="w-full flex-1">
          <SearchBar
            defaultValue={searchText}
            onSearchChange={val => debouncedSearch(val)}
            placeholder="Search to filter (e.g. RNA-SEQ)"
          />
        </div>
      )}
      {setUserNameText && (
        <div className="w-full flex-1">
          <input
            type="text"
            placeholder="Filter By User Name"
            className="w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            defaultValue={userNameText}
            onChange={e => debouncedUserName(e.target.value)}
          />
        </div>
      )}
      <span className="isolate inline-flex rounded-md shadow-sm">
        <button
          type="button"
          onClick={() => handleSelectPipelineType(PipelineViewType.Released)}
          className={classNames(
            "relative inline-flex h-9 items-center rounded-l-md  px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10",
            selectedPipelineType.includes(PipelineViewType.Released)
              ? "bg-indigo-600 text-white hover:bg-indigo-500  hover:shadow-md"
              : "bg-white"
          )}>
          Released
        </button>
        <button
          type="button"
          onClick={() => handleSelectPipelineType(PipelineViewType.MyPipelines)}
          className={classNames(
            "relative -ml-px inline-flex h-9 items-center  px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10",
            selectedPipelineType.includes(PipelineViewType.MyPipelines)
              ? "bg-indigo-600 text-white hover:bg-indigo-500  hover:shadow-md"
              : "bg-white"
          )}>
          My Pipelines
        </button>
        <button
          type="button"
          onClick={() =>
            handleSelectPipelineType(PipelineViewType.SharedWithMe)
          }
          className={classNames(
            "relative -ml-px inline-flex h-9 items-center rounded-r-md  px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10",
            selectedPipelineType.includes(PipelineViewType.SharedWithMe)
              ? "bg-indigo-600 text-white hover:bg-indigo-500  hover:shadow-md"
              : "bg-white"
          )}>
          Shared With Me
        </button>
      </span>
    </div>
  );
}
